import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { parseConditions } from '@asm/features/sync/utils/conditions-parser.utils';
import { OrderHistoryRepository } from '@asm/features/sync/repositories/order-history.repository';
import { ProductRepository } from '@asm/features/sync/repositories/product.repository';
import { B2BUnitHierarchyRepository } from '@asm/features/sync/repositories/b2bunit-hierarchy.repository';
import { WarehouseRepository } from '@asm/features/sync/repositories/warehouse.repository';
import { DocumentTypeRepository } from '@asm/features/sync/repositories/document-type.repository';
import { LocalOrderHistoryItem } from '@asm/features/sync/models/order.model';
import { OfflineOrderOutboxRepository } from '@asm/features/sync/repositories/offline-order-outbox.repository';
import { OfflineOrderOutboxEntry, SyncOrderEntryReceipt } from '@asm/features/sync/models/offline-order-sync.model';

export interface OrderHistoryListParams {
  type: string; // 'orders' | 'quotations'
  page: number;
  pageSize: number;
  search?: string;
}

/**
 * Centraliza toda a lógica de consulta ao IndexedDB para histórico e detalhe de pedidos.
 * Mesmo padrão do DefaultLocalOrderService para place-order.
 *
 * Injetado pelo OrderOfflineHandler para servir:
 *   - GET /order/history/:b2bUnitUid/:customerUid          → list()
 *   - GET /order/history/:b2bUnitUid/:customerUid/:code    → getDetail()
 */
@Injectable({ providedIn: 'root' })
export class DefaultLocalOrderHistoryService {
  private readonly orderHistoryRepo = inject(OrderHistoryRepository);
  private readonly productRepo = inject(ProductRepository);
  private readonly b2bUnitRepo = inject(B2BUnitHierarchyRepository);
  private readonly warehouseRepo = inject(WarehouseRepository);
  private readonly documentTypeRepo = inject(DocumentTypeRepository);
  private readonly outboxRepo = inject(OfflineOrderOutboxRepository);

  /**
   * Lista pedidos/orçamentos com paginação local.
   * Retorna o mesmo shape do endpoint online (OrderHistoryResponse).
   */
  async list(b2bUnitUid: string, customerUid: string, params: OrderHistoryListParams): Promise<object> {
    const isQuotation = params.type === 'quotations';
    const query = params.search?.trim().toLowerCase() ?? '';

    const source$ = query
      ? this.orderHistoryRepo.search(b2bUnitUid, customerUid, isQuotation, query)
      : this.orderHistoryRepo.getByUnitAndCustomer(b2bUnitUid, customerUid, isQuotation);

    const allItems = await firstValueFrom(source$);
    const start = params.page * params.pageSize;
    const pageItems = allItems.slice(start, start + params.pageSize);
    const numberOfPages = Math.max(1, Math.ceil(allItems.length / params.pageSize));

    return {
      items: pageItems.map(item => ({
        orderCode: item.orderCode,
        createdAt: item.createdAt,
        totalPrice: item.totalPrice,
        freightType: item.freightTypeCode ?? null,
        documentType: item.documentTypeDescription ?? null,
        platformStatus: item.platformStatus ?? null,
        isQuotation: item.isQuotation,
        consignmentCode: item.consignmentCode ?? null,
        erpOrderCode: item.erpOrderCode ?? null,
        sapStatus: item.sapStatus ?? null,
        localSyncStatus: item.localSyncStatus,
        isLocal: item.isLocalOrder,
        syncErrorMessage: item.syncErrorMessage ?? undefined,
        clientOrderId: item.clientOrderId ?? null,
        hasChanges: item.hasChanges ?? null,
      })),
      pagination: {
        currentPage: params.page,
        numberOfPages,
        totalNumberOfResults: allItems.length,
      },
    };
  }

  /**
   * Retorna o detalhe de um pedido.
   * Retorna o mesmo shape do endpoint online (OrderDetailDTO).
   */
  async getDetail(b2bUnitUid: string, customerUid: string, orderCode: string): Promise<object | null> {
    const item = await firstValueFrom(this.orderHistoryRepo.getByOrderCode(b2bUnitUid, customerUid, orderCode));

    if (!item) {
      // Fallback: recently synced offline order not yet in history — find via outbox receipt.orderCode
      const allOutbox = await firstValueFrom(this.outboxRepo.getAll());
      const outboxMatch = allOutbox.find(e => e.receipt?.orderCode === orderCode);
      if (outboxMatch) return this.mapOutboxEntryToDetail(outboxMatch);
      return null;
    }

    // Enrich with outbox receipt when this was an offline order placed on this device
    let outboxEntry: OfflineOrderOutboxEntry | null = null;
    if (item.clientOrderId) {
      try {
        outboxEntry = (await firstValueFrom(this.outboxRepo.getByKey(item.clientOrderId))) ?? null;
      } catch {
        outboxEntry = null;
      }
    }

    return this.mapToDetail(item, outboxEntry ?? undefined);
  }

  private async mapToDetail(item: LocalOrderHistoryItem, outboxEntry?: OfflineOrderOutboxEntry): Promise<object> {
    const entries = item.entries ?? [];

    // ── Resolve delivery address from all_b2bunits (keyPath: 'uid') ──────────
    let deliveryAddress: string | null = null;

    const unit = await firstValueFrom(this.b2bUnitRepo.getByKey(item.b2bUnitUid));
    if (unit) {
      const rawAddrs: any[] = (unit as any).rawAddresses ?? [];
      const addr =
        rawAddrs.find((a: any) => a.addressUsage === 'DELIVERY') ??
        rawAddrs.find((a: any) => a.shippingAddress) ??
        rawAddrs[0] ??
        unit.deliveryAddress ??
        null;
      if (addr) {
        const street = [addr.streetname, addr.streetnumber].filter(Boolean).join(', ');
        const city = [addr.town, addr.region?.isocodeShort ?? addr.region].filter(Boolean).join('/');
        deliveryAddress = [street, city].filter(Boolean).join(' — ') || null;
      }
    }

    // ── Resolve warehouse (Centro Fornecedor) from warehouses store ──────────
    const orderWarehouseCode = entries[0]?.warehouseCode ?? null;
    let warehouseDescription: string | null = null;
    if (orderWarehouseCode) {
      const wh = await firstValueFrom(this.warehouseRepo.getByKey(orderWarehouseCode));
      warehouseDescription = (wh as any)?.description ?? null;
    }

    // ── Resolve document type (Documento de Vendas) ──────────────────────────
    let documentTypeDescription: string | null = item.documentTypeDescription ?? null;
    if (!documentTypeDescription) {
      const allDts = await firstValueFrom(this.documentTypeRepo.getAll());
      const match = item.documentTypeCode ? allDts.find(d => d.code === item.documentTypeCode) : null;
      documentTypeDescription = match?.description ?? null;
    }

    const uniqueCodes = [...new Set(entries.map(e => e.productCode).filter(Boolean))];
    const productMap = new Map<string, { mediaUrls?: (string | null | undefined)[] }>();
    await Promise.all(
      uniqueCodes.map(async code => {
        const product = await firstValueFrom(this.productRepo.getByKey(code));
        if (product) productMap.set(code, product);
      }),
    );

    const totalWeight = entries.reduce((s, e) => s + (e.totalWeight ?? 0), 0);
    const totalItems = entries.reduce((s, e) => s + (e.qty ?? 0), 0);
    const nf = item.totalPrice ?? 0;

    // Parse entry conditions to compute per-condition financial totals.
    let zampTotal = 0;
    let zpe1Total = 0;
    let promoTotal = 0;
    let zdcpTotal = 0;
    let zacpTotal = 0;

    for (const e of entries) {
      const parsed = parseConditions(e.conditions ?? []);
      zampTotal += parsed.zampAmount;
      zpe1Total += parsed.zpe1Amount;
      promoTotal += parsed.promoAmount;
      zdcpTotal += parsed.zdcpAmount;
      zacpTotal += parsed.zacpAmount;
    }

    const othersNet = zacpTotal - zdcpTotal;
    const pct = (v: number) => (nf > 0 && Math.abs(v) > 0 ? Math.round((Math.abs(v) / nf) * 1000) / 10 : null);
    const hasConditions = zampTotal > 0 || zpe1Total > 0 || promoTotal > 0 || zdcpTotal > 0 || zacpTotal > 0;

    return {
      orderCode: item.orderCode,
      erpOrderCode: item.erpOrderCode ?? null,
      consignmentCode: item.consignmentCode ?? null,
      createdAt: item.createdAt,
      platformStatus: item.platformStatus ?? null,
      sapStatus: item.sapStatus ?? null,
      localSyncStatus: item.localSyncStatus,
      isLocal: item.isLocalOrder,
      deliveryAddress,
      documentType: documentTypeDescription,
      documentTypeCode: item.documentTypeCode ?? null,
      warehouseCode: orderWarehouseCode,
      warehouseDescription,
      paymentMethodDescription: item.paymentMethodDescription ?? null,
      paymentConditionDescription: item.paymentConditionDescription ?? null,
      freightTypeDescription: item.freightTypeDescription ?? null,
      freightTypeCode: item.freightTypeCode ?? null,
      purchaseOrder: item.purchaseOrder ?? null,
      observation: item.observation ?? null,
      isQuotation: item.isQuotation,
      paymentLink: item.paymentLink ?? null,
      paymentLinkExpiresAt: item.paymentLinkExpiresAt ?? null,
      totalWeight,
      totalItems,
      surchargePercent: hasConditions ? pct(zampTotal) : null,
      surchargeValue: hasConditions && zampTotal > 0 ? zampTotal : null,
      discountPercent: hasConditions ? pct(zpe1Total) : null,
      discountValue: hasConditions && zpe1Total > 0 ? zpe1Total : item.totalDiscounts > 0 ? item.totalDiscounts : null,
      promotionPercent: hasConditions ? pct(promoTotal) : null,
      promotionValue: hasConditions && promoTotal > 0 ? promoTotal : null,
      othersPercent: hasConditions ? pct(othersNet) : null,
      othersValue: hasConditions && Math.abs(othersNet) > 0 ? Math.abs(othersNet) : null,
      othersIsPositive: hasConditions && Math.abs(othersNet) > 0 ? othersNet > 0 : null,
      totalTax: item.totalTax ?? 0,
      totalNF: item.totalPrice ?? 0,
      clientOrderId: item.clientOrderId ?? null,
      ...(() => {
        const mappedEntries = entries.map(e => {
          const syncedProduct = productMap.get(e.productCode);
          const syncedImageUrls = syncedProduct?.mediaUrls?.filter(url => Boolean(String(url ?? '').trim())) ?? [];
          const imageUrls = syncedImageUrls.length > 0 ? syncedImageUrls : (e.imageUrls ?? []);

          const receiptEntry: SyncOrderEntryReceipt | undefined = outboxEntry?.receipt?.entries.find(
            r => r.productCode === e.productCode,
          );

          // status: prefer receipt; fall back to stored entry status
          const entryStatus =
            receiptEntry && receiptEntry.status !== 'ACCEPTED'
              ? receiptEntry.status
              : e.status && e.status !== 'ACCEPTED'
                ? e.status
                : null;

          // requestedQty: receipt wins; then compute from stored originalStockAllocations
          const requestedQty =
            receiptEntry?.requestedQty ??
            (e.originalStockAllocations ? e.originalStockAllocations.reduce((s, a) => s + a.quantity, 0) : null);

          return {
            entryNumber: e.entryNumber,
            productCode: e.productCode,
            productName: e.productName,
            productEan: e.productEan ?? null,
            qty: receiptEntry?.acceptedQty ?? e.qty,
            requestedQty,
            basePrice: e.basePrice ?? 0,
            totalPrice: e.totalPrice ?? 0,
            totalDiscount: e.totalDiscount ?? 0,
            totalSurcharge: 0,
            totalTax: e.totalTax ?? 0,
            totalWeight: e.totalWeight ?? 0,
            ipi: e.ipi ?? 0,
            st: e.st ?? 0,
            icms: e.icms ?? 0,
            warehouseCode: e.warehouseCode ?? null,
            observation: e.observation ?? null,
            rejected: receiptEntry?.status === 'REJECTED' || e.rejected || false,
            message: receiptEntry?.message ?? e.message ?? null,
            status: entryStatus,
            conditions: e.conditions ?? [],
            imageUrls,
            originalStockAllocations: receiptEntry?.originalStockAllocations ?? e.originalStockAllocations ?? null,
            adjustedStockAllocations: receiptEntry?.adjustedStockAllocations ?? e.adjustedStockAllocations ?? null,
          };
        });

        const changedEntries = mappedEntries.filter(e => e.status !== null);
        // hasChanges: prefer outbox receipt → stored item value → derive from entry statuses
        const hasChanges: boolean | null =
          outboxEntry?.receipt?.hasChanges ?? item.hasChanges ?? (changedEntries.length > 0 ? true : null);
        const changedItemCount: number | null =
          outboxEntry?.receipt?.changedItemCount ?? (changedEntries.length > 0 ? changedEntries.length : null);

        return { hasChanges, changedItemCount, entries: mappedEntries };
      })(),
      consignments: (item.consignments ?? []).map(c => ({
        code: c.code,
        erpOrderCode: c.erpOrderCode ?? null,
        sapStatus: c.sapStatus ?? null,
        warehouse: c.warehouse ?? null,
        totalPrice: c.totalPrice ?? 0,
      })),
    };
  }

  /**
   * Carrega o detalhe de um pedido a partir do outbox (pelo clientOrderId).
   * Usado pelo OrderOfflineHandler em modo offline e pelo OrderDetailComponent.
   *
   * Não valida b2bUnitUid/customerUid — o clientOrderId é um UUID imutável,
   * único o suficiente para identificar o pedido sem necessidade de checagem de ownership.
   */
  async getDetailFromOutbox(_b2bUnitUid: string, _customerUid: string, clientOrderId: string): Promise<object | null> {
    const entry = await firstValueFrom(this.outboxRepo.getByKey(clientOrderId));
    if (!entry) return null;
    return this.mapOutboxEntryToDetail(entry);
  }

  private mapOutboxEntryToDetail(entry: OfflineOrderOutboxEntry): object {
    const display = entry.display;
    return {
      orderCode: entry.clientOrderId,
      erpOrderCode: entry.receipt?.orderCode ?? null,
      consignmentCode: null,
      createdAt: entry.createdAt,
      platformStatus: null,
      sapStatus: null,
      localSyncStatus: this.outboxStatusToLocalSyncStatus(entry.status),
      isLocal: true,
      deliveryAddress: null,
      documentType: display.documentTypeDescription,
      documentTypeCode: entry.request.documentTypeCode ?? null,
      warehouseCode: display.warehouseCode,
      warehouseDescription: null,
      paymentMethodDescription: display.paymentMethodDescription,
      paymentConditionDescription: display.paymentConditionDescription,
      freightTypeDescription: display.freightTypeDescription,
      freightTypeCode: entry.request.freightTypeCode ?? null,
      purchaseOrder: entry.request.purchaseOrder ?? null,
      observation: entry.request.observation ?? null,
      isQuotation: entry.request.isQuotation,
      paymentLink: null,
      paymentLinkExpiresAt: null,
      totalWeight: display.entries.reduce((s, e) => s + e.qty, 0),
      totalItems: display.entries.reduce((s, e) => s + e.qty, 0),
      surchargePercent: null,
      surchargeValue: null,
      discountPercent: null,
      discountValue: display.totalDiscounts > 0 ? display.totalDiscounts : null,
      promotionPercent: null,
      promotionValue: null,
      othersPercent: null,
      othersValue: null,
      othersIsPositive: null,
      totalTax: display.totalTax,
      totalNF: display.totalPrice,
      clientOrderId: entry.clientOrderId,
      hasChanges: entry.receipt?.hasChanges ?? null,
      changedItemCount: entry.receipt?.changedItemCount ?? null,
      syncError: entry.status === 'FAILED' ? entry.lastError : null,
      outboxStatus: entry.status,
      entries: display.entries.map((e, idx) => {
        const receiptEntry = entry.receipt?.entries.find(r => r.productCode === e.productCode);
        return {
          entryNumber: idx,
          productCode: e.productCode,
          productName: e.productName,
          productEan: e.productEan,
          qty: receiptEntry?.acceptedQty ?? e.qty,
          requestedQty: receiptEntry?.requestedQty ?? e.qty,
          basePrice: e.basePrice,
          totalPrice: e.totalPrice,
          totalDiscount: 0,
          totalSurcharge: 0,
          totalTax: 0,
          totalWeight: 0,
          ipi: e.ipi,
          st: e.st,
          icms: e.icms,
          warehouseCode: e.warehouseCode,
          observation: e.observation,
          rejected: receiptEntry?.status === 'REJECTED',
          message: receiptEntry?.message ?? null,
          status: receiptEntry?.status ?? null,
          originalStockAllocations: receiptEntry?.originalStockAllocations ?? e.stockAllocations,
          adjustedStockAllocations: receiptEntry?.adjustedStockAllocations ?? null,
          imageUrls: e.imageUrls,
        };
      }),
      consignments: [],
    };
  }

  /**
   * Retorna o histórico de preços de um produto a partir do IndexedDB.
   * Para o modo offline, considera TODOS os pedidos sincronizados (sem filtro de data),
   * excluindo pedidos PENDING (sem preço confirmado pelo backend).
   */
  async getProductPriceHistory(
    b2bUnitUid: string,
    customerUid: string,
    productCode: string,
    page = 0,
    pageSize = 10,
  ): Promise<object> {
    const allOrders = await firstValueFrom(this.orderHistoryRepo.getAll());

    const relevant = allOrders
      .filter(
        o =>
          o.b2bUnitUid === b2bUnitUid &&
          o.customerUid === customerUid &&
          o.localSyncStatus !== 'PENDING' &&
          o.entries.some(e => e.productCode === productCode),
      )
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    const history = relevant.flatMap(order =>
      order.entries
        .filter(e => e.productCode === productCode)
        .map(entry => {
          const parsed = parseConditions(entry.conditions ?? []);
          const othersNet = parsed.zacpAmount - parsed.zdcpAmount;
          const finalUnitPrice = entry.qty > 0 ? (entry.totalPrice ?? 0) / entry.qty : (entry.basePrice ?? 0);

          return {
            createdAt: order.createdAt,
            desiredDeliveryDate: order.desiredDeliveryDate ?? null,
            finalUnitPrice,
            orderCode: order.orderCode,
            erpOrderCode: order.erpOrderCode ?? null,
            documentTypeCode: order.documentTypeCode ?? null,
            documentTypeDescription: order.documentTypeDescription ?? null,
            paymentConditionDescription: order.paymentConditionDescription ?? null,
            qty: entry.qty,
            surchargePercent: parsed.zampPercent,
            discountPercent: parsed.zpe1Percent,
            promotions: parsed.promotions,
            othersValue: Math.abs(othersNet) > 0 ? Math.abs(othersNet) : null,
            othersIsPositive: Math.abs(othersNet) > 0 ? othersNet > 0 : null,
          };
        }),
    );

    const product = await firstValueFrom(this.productRepo.getByKey(productCode));

    // Apply client-side pagination
    const totalCount = history.length;
    const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1;
    const safePage = Math.min(page, Math.max(0, totalPages - 1));
    const start = safePage * pageSize;
    const pagedHistory = pageSize > 0 ? history.slice(start, start + pageSize) : history;

    const mediaUrls: string[] = (product as any)?.mediaUrls ?? [];
    return {
      productCode,
      productName: (product as any)?.name ?? productCode,
      productEan: (product as any)?.ean ?? null,
      imageUrl: mediaUrls[0] ?? null,
      currentPrice: null,
      history: pagedHistory,
      currentPage: safePage,
      pageSize,
      totalPages,
      totalCount,
    };
  }

  private outboxStatusToLocalSyncStatus(status: OfflineOrderOutboxEntry['status']): string {
    switch (status) {
      case 'PENDING':
        return 'PENDING';
      case 'AWAITING_PROCESSING':
        return 'AWAITING_PROCESSING';
      case 'PROCESSING':
        return 'PROCESSING';
      case 'PROCESSED':
        return 'SYNCED';
      case 'PROCESSED_WITH_CHANGES':
        return 'SYNCED_WITH_CHANGES';
      case 'PROCESSED_REJECTED':
        return 'SYNCED_REJECTED';
      case 'FAILED':
        return 'FAILED';
      default:
        return 'PENDING';
    }
  }
}
